import React, { useEffect, useState } from "react";

const BLUE = "#4F7FFA";
const CYAN = "#00BCD4";
const DARK = "#1a1a2e";

const features = [
  {
    icon: "📊",
    title: "Smart Payoff Strategies",
    desc: "Avalanche, Snowball, or Custom — see exactly how much interest you'll save and how many months sooner you'll be free.",
  },
  {
    icon: "📅",
    title: "Variable Payment Schedule",
    desc: "Plan extra payments months in advance. Life changes — your payoff plan should too.",
  },
  {
    icon: "⚖️",
    title: "What-If Scenarios",
    desc: "Compare up to 3 strategies side by side with real numbers from your actual accounts.",
  },
  {
    icon: "💬",
    title: "Ellie AI Advisor",
    desc: "Your personal debt coach, powered by AI and personalized to your exact financial situation.",
  },
  {
    icon: "🏆",
    title: "Milestones for your journey",
    desc: "Celebrate every win on the road to debt freedom. Progress feels better when it's tracked.",
  },
  {
    icon: "🔒",
    title: "Secure Cloud Sync",
    desc: "Your data syncs securely across all your devices. Sign in with Google or Apple.",
  },
];

const steps = [
  { number: "01", title: "Add your debts", desc: "Enter your accounts — credit cards, loans, anything you owe." },
  { number: "02", title: "Pick a strategy", desc: "Choose Avalanche, Snowball, or build a custom payoff order." },
  { number: "03", title: "Track your progress", desc: "Log payments, watch balances drop, and celebrate milestones." },
];

export default function Landing({ onGetStarted }) {
  const [scrolled, setScrolled] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setTimeout(() => setVisible(true), 50);
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={{ ...s.nav, ...(scrolled ? s.navScrolled : {}) }}>
        <div style={s.navInner}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 5 }}>
            <div style={s.logo}>
              <span style={s.logoEl}>EL</span>
              <span style={s.logoDe}>DE</span>
            </div>
            <div style={{ fontSize: 7.5, letterSpacing: 2.5, fontWeight: 700, textAlign: "center" }}>
              <span style={{ color: "#fff" }}>EL</span>
              <span style={{ color: "#4a5568" }}>IMINATE </span>
              <span style={{ color: CYAN }}>DE</span>
              <span style={{ color: "#4a5568" }}>BT</span>
            </div>
          </div>
          <div style={s.navActions}>
            <button style={s.navSignIn} onClick={() => onGetStarted("login")}>Sign In</button>
            <button style={s.navCta} onClick={() => onGetStarted("signup")}>Get Started Free</button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={s.hero}>
        <div style={{ ...s.heroContent, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(24px)", transition: "all 0.7s ease" }}>
          <div style={s.heroBadge}>✦ Free to start · No credit card required</div>
          <h1 style={s.heroTitle}>
            Your debt.<br />Every detail.<br />
            <span style={s.heroAccent}>One place.</span>
          </h1>
          <p style={s.heroSub}>
            ELDE gives you deeper insight into your debt than any bank or finance app will — payoff strategies, interest breakdowns, AI coaching, and milestones to keep you moving.
          </p>
          <div style={s.heroButtons}>
            <button style={s.heroCta} onClick={() => onGetStarted("signup")}>Start for Free →</button>
            <div style={s.heroNote}>Start your journey to debt freedom today</div>
          </div>
        </div>

        {/* Hero visual */}
        <div style={{ ...s.heroVisual, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(32px)", transition: "all 0.9s ease 0.2s" }}>
          <div style={s.mockCard}>
            <div style={s.mockHeader}>
              <div style={s.mockLogo}>
                <span style={{ fontWeight: 900, color: "#fff", fontSize: 13, letterSpacing: 2 }}>EL</span>
                <span style={{ fontWeight: 900, color: CYAN, fontSize: 13, letterSpacing: 2 }}>DE</span>
              </div>
              <div style={{ fontSize: 11, color: "#999" }}>Dashboard</div>
            </div>
            <div style={s.mockStats}>
              {[
                { label: "Total Debt", value: "$33,800", color: "#EF4444" },
                { label: "Monthly Interest", value: "$241", color: "#A855F7" },
                { label: "Debt-Free Date", value: "Mar 2027", color: CYAN },
              ].map(stat => (
                <div key={stat.label} style={s.mockStat}>
                  <div style={s.mockStatLabel}>{stat.label}</div>
                  <div style={{ ...s.mockStatValue, color: stat.color }}>{stat.value}</div>
                </div>
              ))}
            </div>
            <div style={s.mockAccounts}>
              {[
                { name: "Chase Visa", type: "Credit Card", balance: "$8,420", rate: "24.99%", min: "$85.00/mo", interest: "$175.42", due: "Due in 3 days (Apr 7)", dueColor: "#F97316", color: BLUE },
                { name: "Student Loan", type: "Student Loan", balance: "$12,100", rate: "6.5%", min: "$210.00/mo", interest: "$65.54", due: "Due in 12 days (Apr 16)", dueColor: "#10B981", color: "#A855F7" },
                { name: "Auto Loan", type: "Auto Loan", balance: "$4,330", rate: "4.2%", min: "$310.00/mo", interest: "$15.16", due: "Due in 18 days (Apr 22)", dueColor: "#10B981", color: "#10B981" },
              ].map(acct => (
                <div key={acct.name} style={{ borderRadius: 8, background: "#F8F9FC", overflow: "hidden", border: "1px solid #F0F0F5" }}>
                  <div style={{ display: "flex", alignItems: "stretch" }}>
                    <div style={{ width: 4, background: acct.color, flexShrink: 0 }} />
                    <div style={{ padding: "8px 12px", flex: 1 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a2e" }}>{acct.name}</div>
                          <div style={{ fontSize: 9, fontWeight: 600, color: acct.color, textTransform: "uppercase", letterSpacing: 0.5 }}>{acct.type}</div>
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a1a2e" }}>{acct.balance}</div>
                      </div>
                      <div style={{ fontSize: 9, fontWeight: 600, color: acct.dueColor, background: acct.dueColor + "15", borderRadius: 4, padding: "2px 7px", display: "inline-block" }}>📅 {acct.due}</div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", padding: "6px 12px 8px 16px", borderTop: "1px solid #F0F0F5", gap: 4 }}>
                    <div>
                      <div style={{ fontSize: 8, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Interest Rate</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#1a1a2e" }}>{acct.rate}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 8, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Min. Payment</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#1a1a2e" }}>{acct.min}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 8, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>Monthly Interest</div>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#EF4444" }}>{acct.interest}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          {/* Floating badge */}
          <div style={s.floatBadge}>
            <span style={{ fontSize: 18 }}>🏆</span>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#fff" }}>Milestone Unlocked!</div>
              <div style={{ fontSize: 10, color: "#8899BB" }}>First payment logged</div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section style={s.section}>
        <div style={s.sectionInner}>
          <div style={s.sectionTag}>How it works</div>
          <h2 style={s.sectionTitle}>Three steps to debt freedom</h2>
          <div style={s.steps}>
            {steps.map((step, i) => (
              <div key={step.number} style={s.step}>
                <div style={s.stepNumber}>{step.number}</div>
                {i < steps.length - 1 && <div style={s.stepLine} />}
                <div style={s.stepTitle}>{step.title}</div>
                <div style={s.stepDesc}>{step.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section style={{ ...s.section, background: "#0f0f1e" }}>
        <div style={s.sectionInner}>
          <div style={s.sectionTag}>Features</div>
          <h2 style={s.sectionTitle}>The tools your bank doesn't want you to have</h2>
          <div style={s.featureGrid}>
            {features.map(f => (
              <div key={f.title} style={s.featureCard}>
                <div style={s.featureIcon}>{f.icon}</div>
                <div style={s.featureTitle}>{f.title}</div>
                <div style={s.featureDesc}>{f.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* App Screenshots */}
      <section style={s.section}>
        <div style={s.sectionInner}>
          {/* Payoff Plan Mock */}
          <div style={{ display: "flex", alignItems: "center", gap: 60, flexWrap: "wrap", marginBottom: 80 }}>
            <div style={{ flex: "1 1 320px" }}>
              <div style={s.sectionTag}>Payoff Strategies</div>
              <h2 style={{ ...s.sectionTitle, marginBottom: 16 }}>See exactly when you'll be free</h2>
              <p style={{ fontSize: 16, color: "#8899BB", lineHeight: 1.7, marginBottom: 0 }}>
                Choose Avalanche, Snowball, or a custom order. ELDE calculates your payoff date, total interest saved, and monthly payment breakdown in real time.
              </p>
            </div>
            <div style={{ flex: "1 1 380px", display: "flex", justifyContent: "center" }}>
              <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 420, boxShadow: "0 32px 80px rgba(0,0,0,0.2)", border: "1px solid #F0F0F5", overflow: "hidden" }}>
                <div style={{ background: DARK, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>📊 Payoff Plan</div>
                  <div style={{ display: "flex", gap: 6 }}>
                    {["Avalanche", "Snowball", "Custom"].map((s, i) => (
                      <div key={s} style={{ fontSize: 10, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: i === 0 ? BLUE : "rgba(255,255,255,0.1)", color: i === 0 ? "#fff" : "#8899BB" }}>{s}</div>
                    ))}
                  </div>
                </div>
                <div style={{ padding: "16px 20px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", background: "#F0FDF4", borderRadius: 10, padding: "10px 14px", marginBottom: 14, border: "1px solid #BBF7D0" }}>
                    <div style={{ fontSize: 12, color: "#166534" }}>💡 Pay extra $200/mo and save</div>
                    <div style={{ fontSize: 12, fontWeight: 800, color: "#10B981" }}>$4,820 & 14 months</div>
                  </div>
                  {[
                    { name: "Chase Visa", rate: "24.99%", min: "$85", extra: "+$200", total: "$285", rank: 1, focus: true },
                    { name: "Student Loan", rate: "6.5%", min: "$210", extra: "", total: "$210", rank: 2, focus: false },
                    { name: "Auto Loan", rate: "4.2%", min: "$310", extra: "", total: "$310", rank: 3, focus: false },
                  ].map(acct => (
                    <div key={acct.name} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: "1px solid #F8F9FC" }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: acct.focus ? BLUE : "#F3F4F6", color: acct.focus ? "#fff" : "#999", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{acct.rank}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#1a1a2e" }}>{acct.name}</div>
                        <div style={{ fontSize: 11, color: "#999" }}>{acct.rate} APR · Min ${acct.min}</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 14, fontWeight: 800, color: acct.focus ? BLUE : "#1a1a2e" }}>{acct.total}</div>
                        {acct.focus && <div style={{ fontSize: 9, background: "#EEF2FF", color: BLUE, borderRadius: 4, padding: "1px 5px", fontWeight: 700 }}>FOCUS</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Milestones Mock */}
          <div style={{ display: "flex", alignItems: "center", gap: 60, flexWrap: "wrap-reverse" }}>
            <div style={{ flex: "1 1 380px", display: "flex", justifyContent: "center" }}>
              <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 420, boxShadow: "0 32px 80px rgba(0,0,0,0.2)", border: "1px solid #F0F0F5", overflow: "hidden" }}>
                <div style={{ background: DARK, padding: "14px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>🏆 Milestones</div>
                  
                </div>
                <div style={{ padding: "16px 20px" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 16 }}>
                    {[
                      { icon: "🔥", value: "3", label: "Month Streak" },
                      { icon: "💰", value: "$2,332", label: "Principal Paid" },
                      { icon: "📉", value: "$840", label: "Interest Saved" },
                    ].map(s => (
                      <div key={s.label} style={{ background: "#F8F9FC", borderRadius: 10, padding: "10px", textAlign: "center" }}>
                        <div style={{ fontSize: 18, marginBottom: 4 }}>{s.icon}</div>
                        <div style={{ fontSize: 14, fontWeight: 800, color: "#1a1a2e" }}>{s.value}</div>
                        <div style={{ fontSize: 9, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1a2e" }}>Overall Progress</div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: BLUE }}>19% complete</div>
                    </div>
                    <div style={{ height: 8, background: "#F0F0F5", borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: "19%", background: `linear-gradient(90deg, ${BLUE}, #A855F7)`, borderRadius: 4 }} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 14 }}>
                    {[
                      { icon: "🎯", title: "First Payment", earned: true },
                      { icon: "🏁", title: "Under $10K", earned: true },
                      { icon: "⚡", title: "Extra Payment", earned: true },
                      { icon: "🚀", title: "Final Stretch", earned: false },
                    ].map(m => (
                      <div key={m.title} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 10px", borderRadius: 8, background: m.earned ? "#F0FDF4" : "#F8F9FC", border: `1px solid ${m.earned ? "#BBF7D0" : "#F0F0F5"}`, opacity: m.earned ? 1 : 0.5 }}>
                        <span style={{ fontSize: 16 }}>{m.icon}</span>
                        <div style={{ fontSize: 11, fontWeight: 600, color: m.earned ? "#166534" : "#999" }}>{m.title}</div>
                        {m.earned && <div style={{ marginLeft: "auto", fontSize: 10, color: "#10B981", fontWeight: 700 }}>✓</div>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ flex: "1 1 320px" }}>
              <div style={s.sectionTag}>Milestones</div>
              <h2 style={{ ...s.sectionTitle, marginBottom: 16 }}>Celebrate every win</h2>
              <p style={{ fontSize: 16, color: "#8899BB", lineHeight: 1.7, marginBottom: 0 }}>
                Dozens of milestones tracking your journey from first payment to debt freedom. Streaks, payoff achievements, interest saved — every step forward counts.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ellie AI Section */}
      <section style={{ ...s.section, background: "#0f0f1e" }}>
        <div style={s.sectionInner}>
          <div style={{ display: "flex", alignItems: "center", gap: 60, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 320px" }}>
              <div style={s.sectionTag}>Ellie AI Advisor</div>
              <h2 style={{ ...s.sectionTitle, marginBottom: 16 }}>Your personal debt coach, powered by AI</h2>
              <p style={{ fontSize: 16, color: "#8899BB", lineHeight: 1.7, marginBottom: 0 }}>
                Ask Ellie anything about your debt. She knows your exact balances, rates, and payment history — and gives you personalized advice based on your real numbers, not generic tips.
              </p>
            </div>
            <div style={{ flex: "1 1 380px", display: "flex", justifyContent: "center" }}>
              <div style={{ background: "#fff", borderRadius: 20, width: "100%", maxWidth: 400, boxShadow: "0 32px 80px rgba(0,0,0,0.3)", border: "1px solid #F0F0F5", overflow: "hidden" }}>
                {/* Chat header */}
                <div style={{ background: DARK, padding: "14px 20px", display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 34, height: 34, borderRadius: 4, background: "#000", border: `2px solid ${CYAN}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 900, color: "#fff" }}>EL</span>
                    <span style={{ fontSize: 9, fontWeight: 900, color: CYAN }}>DE</span>
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Ellie</div>
                    <div style={{ fontSize: 10, color: "#8899BB" }}>ELDE Financial Advisor · AI powered</div>
                  </div>
                </div>
                {/* Chat messages */}
                <div style={{ padding: "16px 16px 12px", display: "flex", flexDirection: "column", gap: 12 }}>
                  {/* User message */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ background: BLUE, color: "#fff", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", fontSize: 13, maxWidth: "80%" }}>
                      Which debt should I pay off first?
                    </div>
                  </div>
                  {/* Ellie response */}
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ width: 24, height: 24, borderRadius: 3, background: "#000", border: `1.5px solid ${CYAN}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 7, fontWeight: 900 }}>
                      <span style={{ color: "#fff" }}>EL</span><span style={{ color: CYAN }}>DE</span>
                    </div>
                    <div style={{ background: "#F3F4F6", borderRadius: "14px 14px 14px 4px", padding: "10px 14px", fontSize: 13, color: "#1a1a2e", lineHeight: 1.6, maxWidth: "85%" }}>
                      Focus on your <strong>Chase Visa</strong> first — at 24.99% APR it's costing you <strong>$175/mo</strong> in interest alone. Paying an extra $200/mo clears it in 14 months and saves you <strong>$4,820</strong> in interest.
                    </div>
                  </div>
                  {/* User follow-up */}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <div style={{ background: BLUE, color: "#fff", borderRadius: "14px 14px 4px 14px", padding: "10px 14px", fontSize: 13, maxWidth: "80%" }}>
                      What if I can only do $100 extra?
                    </div>
                  </div>
                  {/* Ellie response 2 */}
                  <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
                    <div style={{ width: 24, height: 24, borderRadius: 3, background: "#000", border: `1.5px solid ${CYAN}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, fontSize: 7, fontWeight: 900 }}>
                      <span style={{ color: "#fff" }}>EL</span><span style={{ color: CYAN }}>DE</span>
                    </div>
                    <div style={{ background: "#F3F4F6", borderRadius: "14px 14px 14px 4px", padding: "10px 14px", fontSize: 13, color: "#1a1a2e", lineHeight: 1.6, maxWidth: "85%" }}>
                      Still worth it! $100 extra saves you <strong>$2,340</strong> and cuts 8 months off your payoff. Every dollar you put toward Chase Visa right now is working harder than anywhere else.
                    </div>
                  </div>
                </div>
                {/* Input bar */}
                <div style={{ padding: "8px 16px 14px", borderTop: "1px solid #F0F0F5", display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, background: "#F8F9FC", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#999" }}>Ask Ellie anything...</div>
                  <div style={{ background: BLUE, borderRadius: 10, width: 38, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>➤</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section style={s.section}>
        <div style={s.sectionInner}>
          <div style={s.sectionTag}>Pricing</div>
          <h2 style={s.sectionTitle}>Simple, honest pricing</h2>
          <div style={s.pricingGrid}>
            {/* Free */}
            <div style={s.pricingCard}>
              <div style={s.pricingName}>Free</div>
              <div style={s.pricingPrice}>$0<span style={s.pricingPer}>/forever</span></div>
              <div style={s.pricingDesc}>Everything you need to get started</div>
              <div style={s.pricingDivider} />
              {["Up to 3 debt accounts", "Payoff strategies", "Payment tracking", "Transaction history", "Debt calculators"].map(f => (
                <div key={f} style={s.pricingFeature}><span style={{ color: "#10B981" }}>✓</span> {f}</div>
              ))}
              <button style={s.pricingBtn} onClick={() => onGetStarted("signup")}>Get Started Free</button>
            </div>

            {/* Pro Monthly */}
            <div style={{ ...s.pricingCard, ...s.pricingCardPro }}>
              <div style={s.proBadge}>Most Popular</div>
              <div style={{ ...s.pricingName, color: "#fff" }}>Pro Monthly</div>
              <div style={{ ...s.pricingPrice, color: "#fff" }}>$4.99<span style={{ ...s.pricingPer, color: "#8899BB" }}>/mo</span></div>
              <div style={{ ...s.pricingDesc, color: "#8899BB" }}>Full access, billed monthly</div>
              <div style={{ ...s.pricingDivider, background: "rgba(255,255,255,0.1)" }} />
              {["Everything in Free", "Unlimited accounts", "Ellie AI Advisor", "What-If Scenarios", "Variable Payment Schedule", "Milestones for your journey"].map(f => (
                <div key={f} style={{ ...s.pricingFeature, color: "#cdd6f4" }}><span style={{ color: CYAN }}>✓</span> {f}</div>
              ))}
              <button style={{ ...s.pricingBtn, ...s.pricingBtnPro }} onClick={() => onGetStarted("signup")}>Get Started</button>
            </div>

            {/* Pro Yearly */}
            <div style={{ ...s.pricingCard, border: "1.5px solid #10B981", background: "#0d1f1a", position: "relative" }}>
              <div style={{ ...s.proBadge, background: "#10B981" }}>Save 42%</div>
              <div style={{ ...s.pricingName, color: "#fff" }}>Pro Yearly</div>
              <div style={{ ...s.pricingPrice, color: "#fff" }}>$34.99<span style={{ ...s.pricingPer, color: "#8899BB" }}>/yr</span></div>
              <div style={{ ...s.pricingDesc, color: "#8899BB" }}>Full access, billed annually</div>
              <div style={{ ...s.pricingDivider, background: "rgba(255,255,255,0.1)" }} />
              {["Everything in Free", "Unlimited accounts", "Ellie AI Advisor", "What-If Scenarios", "Variable Payment Schedule", "Milestones for your journey"].map(f => (
                <div key={f} style={{ ...s.pricingFeature, color: "#cdd6f4" }}><span style={{ color: "#10B981" }}>✓</span> {f}</div>
              ))}
              <button style={{ ...s.pricingBtn, background: "linear-gradient(135deg, #10B981, #059669)", color: "#fff", boxShadow: "0 4px 20px rgba(16,185,129,0.4)" }} onClick={() => onGetStarted("signup")}>Get Started</button>
              <div style={{ textAlign: "center", fontSize: 11, color: "#8899BB", marginTop: 10 }}>Equivalent to $2.92/mo · Save 42%</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={s.ctaSection}>
        <div style={s.ctaInner}>
          <h2 style={s.ctaTitle}>Ready to become debt-free?</h2>
          <p style={s.ctaSub}>Join ELDE today and take the first step toward financial freedom.</p>
          <button style={s.ctaBtn} onClick={() => onGetStarted("signup")}>Get Started — It's Free →</button>
        </div>
      </section>

      {/* Footer */}
      <footer style={s.footer}>
        <div style={s.footerInner}>
          <div style={s.logo}>
            <span style={s.logoEl}>EL</span>
            <span style={s.logoDe}>DE</span>
          </div>
          <div style={s.footerLinks}>
            <button onClick={() => window.location.href = "/privacy.html"} style={{ ...s.footerLink, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Privacy Policy</button>
            <button onClick={() => window.location.href = "/terms.html"} style={{ ...s.footerLink, background: "none", border: "none", cursor: "pointer", padding: 0 }}>Terms of Service</button>
            <a href="mailto:elde.support@gmail.com" style={s.footerLink}>Contact</a>
          </div>
          <div style={s.footerCopy}>© {new Date().getFullYear()} ELDE. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}

const s = {
  page: { fontFamily: "'Segoe UI', system-ui, sans-serif", background: "#121220", color: "#fff", overflowX: "hidden" },

  // Nav
  nav: { position: "fixed", top: 0, left: 0, right: 0, zIndex: 100, padding: "0 24px", transition: "all 0.3s" },
  navScrolled: { background: "rgba(18,18,32,0.95)", backdropFilter: "blur(12px)", boxShadow: "0 1px 20px rgba(0,0,0,0.3)" },
  navInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", height: 68 },
  logo: { display: "inline-flex", alignItems: "center", border: `2.5px solid ${CYAN}`, borderRadius: 4, padding: "2px 8px", background: "#000" },
  logoEl: { fontWeight: 900, color: "#fff", fontSize: 22, letterSpacing: 2 },
  logoDe: { fontWeight: 900, color: CYAN, fontSize: 22, letterSpacing: 2 },
  navActions: { display: "flex", alignItems: "center", gap: 12 },
  navSignIn: { background: "none", border: "none", color: "#8899BB", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "8px 16px" },
  navCta: { background: BLUE, color: "#fff", border: "none", borderRadius: 8, padding: "9px 20px", fontSize: 14, fontWeight: 700, cursor: "pointer" },

  // Hero
  hero: { minHeight: "auto", display: "flex", alignItems: "center", gap: 60, maxWidth: 1100, margin: "0 auto", padding: "120px 24px 80px", flexWrap: "wrap", position: "relative" },
  heroContent: { flex: "1 1 420px" },
  heroBadge: { display: "inline-block", fontSize: 12, fontWeight: 600, color: CYAN, background: "rgba(0,188,212,0.1)", border: "1px solid rgba(0,188,212,0.2)", borderRadius: 20, padding: "5px 14px", marginBottom: 24, letterSpacing: 0.3 },
  heroTitle: { fontSize: "clamp(40px, 6vw, 72px)", fontWeight: 900, lineHeight: 1.05, margin: "0 0 20px", letterSpacing: -2, color: "#fff" },
  heroAccent: { color: BLUE },
  heroSub: { fontSize: 18, color: "#8899BB", lineHeight: 1.7, marginBottom: 36, maxWidth: 480 },
  heroButtons: { display: "flex", flexDirection: "column", gap: 12, alignItems: "flex-start" },
  heroCta: { background: `linear-gradient(135deg, ${BLUE}, #3B6FE8)`, color: "#fff", border: "none", borderRadius: 12, padding: "16px 32px", fontSize: 16, fontWeight: 700, cursor: "pointer", boxShadow: `0 8px 32px ${BLUE}44` },
  heroNote: { fontSize: 13, color: "#4a5568" },

  // Mock card
  heroVisual: { flex: "1 1 380px", position: "relative", display: "flex", justifyContent: "center" },
  mockCard: { background: "#fff", borderRadius: 20, width: "100%", maxWidth: 380, boxShadow: "0 32px 80px rgba(0,0,0,0.2)", border: "1px solid #F0F0F5", overflow: "hidden" },
  mockHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", background: DARK, padding: "16px 20px", marginBottom: 0 },
  mockLogo: { display: "flex", border: `1.5px solid ${CYAN}`, borderRadius: 4, padding: "1px 7px", background: "#000" },
  mockStats: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20, padding: "20px 20px 0" },
  mockStat: { background: "#F8F9FC", borderRadius: 10, padding: "10px 12px" },
  mockStatLabel: { fontSize: 8, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  mockStatValue: { fontSize: 15, fontWeight: 800, lineHeight: 1.2 },
  mockAccounts: { display: "flex", flexDirection: "column", gap: 12, padding: "0 20px 20px" },
  mockAcct: {},
  mockAcctTop: { display: "flex", justifyContent: "space-between", marginBottom: 6 },
  mockAcctName: { fontSize: 12, color: "#1a1a2e", fontWeight: 600 },
  mockAcctBal: { fontSize: 12, fontWeight: 700 },
  mockBar: { height: 5, background: "#F0F0F5", borderRadius: 3, overflow: "hidden" },
  mockBarFill: { height: "100%", borderRadius: 3 },
  floatBadge: { position: "absolute", bottom: -16, right: -16, background: DARK, borderRadius: 14, padding: "10px 16px", display: "flex", alignItems: "center", gap: 10, boxShadow: "0 8px 32px rgba(0,0,0,0.4)", border: "1px solid rgba(255,255,255,0.08)" },

  // Sections
  section: { padding: "96px 24px" },
  sectionInner: { maxWidth: 1100, margin: "0 auto" },
  sectionTag: { fontSize: 12, fontWeight: 700, color: CYAN, textTransform: "uppercase", letterSpacing: 1.5, marginBottom: 12 },
  sectionTitle: { fontSize: "clamp(28px, 4vw, 42px)", fontWeight: 900, letterSpacing: -1, marginBottom: 48, color: "#fff" },

  // Steps
  steps: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 40 },
  step: { position: "relative" },
  stepNumber: { fontSize: 48, fontWeight: 900, color: "rgba(0,188,212,0.75)", letterSpacing: -2, marginBottom: 8, lineHeight: 1 },
  stepLine: { position: "absolute", top: 24, left: "100%", width: "100%", height: 2, background: "rgba(255,255,255,0.05)", display: "none" },
  stepTitle: { fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 8 },
  stepDesc: { fontSize: 14, color: "#8899BB", lineHeight: 1.7 },

  // Features
  featureGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 24 },
  featureCard: { background: DARK, borderRadius: 16, padding: "28px 24px", border: "1px solid rgba(255,255,255,0.06)" },
  featureIcon: { fontSize: 32, marginBottom: 14 },
  featureTitle: { fontSize: 16, fontWeight: 800, color: "#fff", marginBottom: 8 },
  featureDesc: { fontSize: 14, color: "#8899BB", lineHeight: 1.7 },

  // Pricing
  pricingGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24, maxWidth: 960, margin: "0 auto" },
  pricingCard: { borderRadius: 20, padding: "32px 28px", border: "1px solid rgba(255,255,255,0.08)", background: DARK },
  pricingCardPro: { background: "#1e2a4a", border: `1.5px solid ${BLUE}`, boxShadow: `0 16px 48px ${BLUE}22`, position: "relative" },
  proBadge: { position: "absolute", top: -12, left: "50%", transform: "translateX(-50%)", background: `linear-gradient(135deg, ${BLUE}, ${CYAN})`, color: "#fff", fontSize: 11, fontWeight: 700, padding: "4px 14px", borderRadius: 20, whiteSpace: "nowrap" },
  pricingName: { fontSize: 18, fontWeight: 800, color: "#fff", marginBottom: 8 },
  pricingPrice: { fontSize: 40, fontWeight: 900, color: "#fff", letterSpacing: -1, marginBottom: 6 },
  pricingPer: { fontSize: 16, fontWeight: 500, color: "#8899BB" },
  pricingDesc: { fontSize: 13, color: "#8899BB", marginBottom: 20 },
  pricingDivider: { height: 1, background: "rgba(255,255,255,0.08)", marginBottom: 20 },
  pricingFeature: { fontSize: 13, color: "#cdd6f4", marginBottom: 10, display: "flex", gap: 8, alignItems: "center" },
  pricingBtn: { width: "100%", background: "rgba(255,255,255,0.08)", color: "#fff", border: "none", borderRadius: 10, padding: "13px 0", fontSize: 14, fontWeight: 700, cursor: "pointer", marginTop: 24 },
  pricingBtnPro: { background: `linear-gradient(135deg, ${BLUE}, #3B6FE8)`, color: "#fff", boxShadow: `0 4px 20px ${BLUE}44` },

  // CTA
  ctaSection: { background: "#0d0d1a", padding: "96px 24px", borderTop: "1px solid rgba(255,255,255,0.05)" },
  ctaInner: { maxWidth: 600, margin: "0 auto", textAlign: "center" },
  ctaTitle: { fontSize: "clamp(28px, 3.5vw, 48px)", fontWeight: 900, color: "#fff", letterSpacing: -1.5, marginBottom: 16, whiteSpace: "nowrap" },
  ctaSub: { fontSize: 16, color: "#8899BB", marginBottom: 36, lineHeight: 1.7 },
  ctaBtn: { background: `linear-gradient(135deg, ${BLUE}, #3B6FE8)`, color: "#fff", border: "none", borderRadius: 12, padding: "16px 36px", fontSize: 16, fontWeight: 700, cursor: "pointer", boxShadow: `0 8px 32px ${BLUE}44` },

  // Footer
  footer: { background: "#0d0d1a", padding: "40px 24px", borderTop: "1px solid rgba(255,255,255,0.05)" },
  footerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 },
  footerLinks: { display: "flex", gap: 24 },
  footerLink: { fontSize: 13, color: "#8899BB", textDecoration: "none", fontWeight: 500 },
  footerCopy: { fontSize: 12, color: "#4a5568" },
};
